import { useEffect, useState, type ReactNode } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAuthStore } from "@/stores/auth";

export interface AuthProviderProps {
  children: ReactNode;
  /** Custom slot shown while the initial /me call is in flight. */
  loadingFallback?: ReactNode;
}

/**
 * Bootstraps the session: on mount calls /api/auth/me, mirrors the response
 * (or null on 401) into the Zustand auth-slice.
 *
 * Render gate: children only render AFTER the auth store has been hydrated
 * with the /me result. Without this gate, an authenticated refresh briefly
 * showed `userId=null` to descendants (the store-write `useEffect` runs
 * after the render where `isFetched` first became true), so `AuthGuard`
 * redirected to /login one cycle before being corrected — exactly the
 * visible "login form on refresh" bug.
 */
export function AuthProvider({
  children,
  loadingFallback = null,
}: AuthProviderProps) {
  const setUser = useAuthStore((s) => s.setUser);
  const { data, isFetched } = useCurrentUser();
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isFetched) return;
    setUser(data ?? null);
    setHydrated(true);
  }, [data, isFetched, setUser]);

  if (!hydrated) {
    return <>{loadingFallback}</>;
  }
  return <>{children}</>;
}
